import React from 'react';
import { Link } from 'react-router-dom';
import {
  Shield,
  ArrowRight,
  AlertTriangle,
  CheckCircle,
  Clock,
  FileText,
  Activity,
  Lock,
  Scale,
  Brain,
  Stethoscope,
  Calculator,
  Heart,
  ShieldCheck,
  Users,
  Mail,
  MapPin,
  Wrench,
  Wifi,
  Package,
  ClipboardList,
} from 'lucide-react';

// ─── Data ────────────────────────────────────────────────────────────────────

const incidents = [
  {
    org: 'Baltimore Medical System',
    date: 'July 2025',
    type: 'Ransomware Attack',
    detail: 'Patient SSNs and medical records exposed. Class action lawsuit filed.',
    color: 'border-red-400/40 bg-red-900/20',
    badge: 'bg-red-500/20 text-red-300',
  },
  {
    org: 'Baltimore City Community College',
    date: '2025',
    type: 'AI Bot Fraud',
    detail: '$264,000 lost to AI-powered financial aid fraud. Audited by Maryland state.',
    color: 'border-amber-400/40 bg-amber-900/20',
    badge: 'bg-amber-500/20 text-amber-300',
  },
  {
    org: 'Frederick Health',
    date: 'January 2025',
    type: 'Ransomware Attack',
    detail: '934,000 patient records exposed in a ransomware attack.',
    color: 'border-orange-400/40 bg-orange-900/20',
    badge: 'bg-orange-500/20 text-orange-300',
  },
];

const steps = [
  {
    num: '01',
    icon: Clock,
    title: 'Free CyvaCheck',
    desc: 'We run a live security check on your domain in 20 minutes — no obligation, no jargon.',
  },
  {
    num: '02',
    icon: FileText,
    title: 'CyvaScore Report',
    desc: 'If gaps are found, we deliver a full assessment report within 48 hours.',
  },
  {
    num: '03',
    icon: Activity,
    title: 'Ongoing Protection',
    desc: 'Monthly monitoring keeps you covered as threats evolve.',
  },
];

const serviceCategories = [
  {
    category: 'Cybersecurity & Compliance',
    services: [
      {
        icon: Shield,
        name: 'CyvaCheck',
        desc: 'A free 20-minute live security check on your business domain. We show you exactly where you are exposed — email security, credential leaks, and more. No obligation, no jargon, just answers.',
        price: 'Free',
        priceNote: 'Always free',
        highlight: true,
        poweredBy: true,
      },
      {
        icon: FileText,
        name: 'CyvaScore',
        desc: 'A complete security health assessment for your business. We run five automated checks, conduct a structured intake review, and deliver a plain-English report within 48 hours — with prioritized findings, exact fix steps, and your compliance exposure mapped out.',
        price: 'Custom assessment',
        priceNote: 'One-time',
        highlight: false,
        poweredBy: true,
      },
      {
        icon: Activity,
        name: 'CyvaGuard Essentials',
        desc: 'Monthly automated monitoring that keeps you protected as threats evolve. Includes monthly security score tracking, AI-generated phishing simulations for your staff, dark web credential monitoring, and a one-page executive summary every month.',
        price: 'Monthly retainer',
        priceNote: 'Ongoing',
        highlight: false,
        poweredBy: true,
      },
      {
        icon: Lock,
        name: 'CyvaGuard Pro',
        desc: 'Everything in Essentials plus quarterly re-assessments, vendor risk reviews, AI-drafted security policy templates, and a dedicated channel for security questions answered within 24 hours. Built for practices and firms that need ongoing advisory support.',
        price: 'Monthly retainer',
        priceNote: 'Ongoing',
        highlight: false,
        poweredBy: true,
      },
      {
        icon: Scale,
        name: 'CyvaComply',
        desc: 'Structured compliance gap closure for businesses facing a specific regulatory deadline. We map your current controls against HIPAA, CMMC, SOC 2, or PCI-DSS, identify every gap, and deliver all required policy documents and evidence templates — reviewed and signed off by a federal cybersecurity veteran.',
        price: 'Project-based',
        priceNote: 'Scoped per engagement',
        highlight: false,
      },
      {
        icon: Brain,
        name: 'CyvaAware',
        desc: 'A one-hour staff workshop on AI-powered threats — phishing emails, deepfake voice scams, and bot fraud. Built around real local incidents including the Baltimore City Community College AI bot attack. Delivered in-person or remotely. Includes a policy document your team keeps.',
        price: 'Per session',
        priceNote: 'Group or individual',
        highlight: false,
      },
      {
        icon: ClipboardList,
        name: 'CMMC Compliance Readiness',
        desc: 'CMMC self-assessment requirements are in effect now for defense subcontractors. We assess your posture against NIST SP 800-171 controls, calculate your SPRS score, build your System Security Plan, and create a remediation roadmap — so you stay contract-eligible.',
        price: 'Project-based',
        priceNote: 'Scoped per engagement',
        highlight: false,
      },
    ],
  },
  {
    category: 'IT Consulting',
    services: [
      {
        icon: Wrench,
        name: 'IT Setup and Support',
        desc: 'Hands-on IT support for small businesses — device setup, troubleshooting, software configuration, and day-to-day technical help when you need it. No contract required.',
        price: 'Hourly',
        priceNote: 'As needed',
        highlight: false,
      },
      {
        icon: Wifi,
        name: 'Network Management',
        desc: 'Professional network configuration and ongoing management for your office. We set it up right, keep it running, and make sure it stays secure — so you can focus on your business.',
        price: 'Monthly retainer',
        priceNote: 'Ongoing',
        highlight: false,
      },
      {
        icon: Package,
        name: 'Combined IT & Security Package',
        desc: 'Monthly IT support combined with CyvaGuard security monitoring — our most comprehensive offering for businesses that want one trusted vendor for everything. One bill, one point of contact, complete coverage.',
        price: 'Monthly retainer',
        priceNote: 'Most popular',
        highlight: false,
      },
    ],
  },
];

const industries = [
  { icon: Stethoscope, label: 'Dental Practices' },
  { icon: Scale,       label: 'Law Firms' },
  { icon: Calculator,  label: 'CPA Firms' },
  { icon: Heart,       label: 'Nonprofits' },
  { icon: ShieldCheck, label: 'Defense Contractors' },
  { icon: Users,       label: 'Small Businesses' },
];

const stats = [
  { label: 'Federal cybersecurity background', sub: 'Defense and intelligence agency experience · NIST RMF' },
  { label: 'Certified IT professionals', sub: 'CompTIA A+ · Security+ certified team members' },
  { label: 'Maryland WOSB certified', sub: 'Woman-owned, Maryland-based' },
  { label: 'AI-powered tools', sub: 'Enterprise quality at small business prices' },
];

// ─── Component ───────────────────────────────────────────────────────────────

const Home: React.FC = () => {
  return (
    <div className="w-full">

      {/* ═══════════════════════════════════════════════════════
          1. HERO
         ═══════════════════════════════════════════════════════ */}
      <section className="bg-white py-20 md:py-28 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-4xl md:text-6xl font-extrabold text-[#001a4d] leading-[1.08] tracking-tight mb-6">
            Cybersecurity for Baltimore Small Businesses
          </h1>
          <p className="text-lg md:text-xl text-gray-600 leading-relaxed max-w-2xl mx-auto mb-10">
            Protect your practice, your clients, and your reputation —
            without an enterprise budget.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
            <a
              href="mailto:info@cyvantixsolutions.com?subject=Book%20Free%20CyvaCheck"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-[#0B8A8F] text-white font-bold rounded-xl hover:bg-[#097a7f] transition-all shadow-md hover:shadow-lg text-base"
            >
              Book Your Free CyvaCheck
              <ArrowRight size={18} />
            </a>
            <Link
              to="/services"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 border-2 border-[#001a4d] text-[#001a4d] font-bold rounded-xl hover:bg-[#001a4d] hover:text-white transition-all text-base"
            >
              See Our Services
            </Link>
          </div>

          <p className="text-sm text-gray-500 font-medium">
            Baltimore-based&nbsp;·&nbsp;Woman-owned&nbsp;·&nbsp;Federal cybersecurity expertise
          </p>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════
          2. LOCAL THREATS — navy bg
         ═══════════════════════════════════════════════════════ */}
      <section className="bg-[#001a4d] py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-red-500/20 border border-red-400/30 mb-4">
              <AlertTriangle size={15} className="text-red-400" />
              <span className="text-red-300 text-xs font-bold uppercase tracking-wide">Local Threat Report</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-extrabold text-white">
              It's already happening in Baltimore
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
            {incidents.map((inc, i) => (
              <div
                key={i}
                className={`rounded-2xl border p-6 ${inc.color}`}
              >
                <div className="flex items-start justify-between mb-3">
                  <span className={`text-xs font-bold uppercase tracking-wide px-2.5 py-1 rounded-full ${inc.badge}`}>
                    {inc.type}
                  </span>
                  <span className="text-white/50 text-xs">{inc.date}</span>
                </div>
                <h3 className="text-white font-bold text-base mb-2">{inc.org}</h3>
                <p className="text-white/70 text-sm leading-relaxed">{inc.detail}</p>
              </div>
            ))}
          </div>

          <div className="mt-4 mb-6 mx-auto max-w-3xl rounded-2xl border border-amber-400/30 bg-amber-500/10 px-6 py-4 text-center">
            <p className="text-amber-200 text-sm leading-relaxed">
              <span className="font-bold text-amber-100">Defense contractors:</span>{' '}
              With $57 billion in Maryland defense contract activity, CMMC compliance is a live
              requirement for defense contractors. While the DoD suspended its Phase 2 third-party
              assessment deadline in July 2026, self-assessment requirements are in effect now —
              and the 110 NIST SP 800-171 controls must be implemented to maintain contract eligibility.
            </p>
          </div>

          <p className="text-center text-white/60 text-sm italic">
            These aren't distant threats. They happened here, last year, to organizations like yours.
          </p>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════
          3. HOW IT WORKS — 3 steps
         ═══════════════════════════════════════════════════════ */}
      <section className="bg-gray-50 py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-14">
            <h2 className="text-3xl md:text-4xl font-extrabold text-[#001a4d] mb-3">
              How it works
            </h2>
            <p className="text-gray-600 text-base">
              Three straightforward steps from zero to protected.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {steps.map((step, i) => {
              const Icon = step.icon;
              return (
                <div key={i} className="relative bg-white rounded-2xl p-8 shadow-sm border border-gray-100 text-center">
                  {/* Connector line (desktop) */}
                  {i < steps.length - 1 && (
                    <div className="hidden md:block absolute top-12 left-full w-8 h-0.5 bg-[#0B8A8F]/30 z-10" style={{ transform: 'translateX(-50%)' }} />
                  )}
                  <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-[#001a4d] mb-4">
                    <Icon className="w-7 h-7 text-white" strokeWidth={1.8} />
                  </div>
                  <div className="text-xs font-black text-[#0B8A8F] tracking-widest mb-2">STEP {step.num}</div>
                  <h3 className="text-lg font-extrabold text-[#001a4d] mb-3">{step.title}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{step.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════
          4. SERVICES — categorised
         ═══════════════════════════════════════════════════════ */}
      <section className="bg-white py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-14">
            <h2 className="text-3xl md:text-4xl font-extrabold text-[#001a4d] mb-3">
              Our Services
            </h2>
            <p className="text-gray-600 text-base max-w-xl mx-auto">
              Enterprise-quality cybersecurity and IT support — built for small businesses.
            </p>
          </div>

          {serviceCategories.map((cat, ci) => (
            <div key={ci} className={ci > 0 ? 'mt-14' : ''}>
              {/* Category label */}
              <div className="flex items-center gap-4 mb-6">
                <span className="text-xs font-black uppercase tracking-widest text-[#0B8A8F]">
                  {cat.category}
                </span>
                <div className="flex-1 h-px bg-gray-100" />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {cat.services.map((svc, i) => {
                  const Icon = svc.icon;
                  return (
                    <div
                      key={i}
                      className={`rounded-2xl p-6 border transition-all hover:shadow-md ${
                        svc.highlight
                          ? 'bg-[#0B8A8F] border-[#0B8A8F] text-white'
                          : 'bg-white border-gray-200 hover:border-[#0B8A8F]/40'
                      }`}
                    >
                      <div className={`inline-flex items-center justify-center w-11 h-11 rounded-xl mb-4 ${
                        svc.highlight ? 'bg-white/20' : 'bg-[#001a4d]'
                      }`}>
                        <Icon className="w-6 h-6 text-white" strokeWidth={1.8} />
                      </div>
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <h3 className={`font-extrabold text-lg ${svc.highlight ? 'text-white' : 'text-[#001a4d]'}`}>
                          {svc.name}
                        </h3>
                        {(svc as any).poweredBy && (
                          <span className={`text-[10px] font-black uppercase tracking-wider px-2 py-0.5 rounded-full ${
                            svc.highlight ? 'bg-white/20 text-white' : 'bg-[#001a4d]/10 text-[#001a4d]'
                          }`}>
                            CyvaForge
                          </span>
                        )}
                      </div>
                      <p className={`text-sm mb-4 leading-relaxed ${svc.highlight ? 'text-white/80' : 'text-gray-500'}`}>
                        {svc.desc}
                      </p>
                      <div className="flex items-end justify-between">
                        <div>
                          <div className={`text-base font-bold ${svc.highlight ? 'text-white' : 'text-[#001a4d]'}`}>
                            {svc.price}
                          </div>
                          <div className={`text-xs ${svc.highlight ? 'text-white/60' : 'text-gray-400'}`}>
                            {svc.priceNote}
                          </div>
                        </div>
                        {svc.highlight && (
                          <span className="text-xs font-bold bg-white/20 text-white px-3 py-1 rounded-full">
                            Start here
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}

          <p className="text-center text-gray-500 text-sm mt-10">
            Contact us for a custom quote tailored to your business size and needs.{' '}
            <a href="mailto:info@cyvantixsolutions.com" className="text-[#0B8A8F] font-semibold hover:underline">
              info@cyvantixsolutions.com
            </a>
          </p>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════
          5. WHO WE SERVE
         ═══════════════════════════════════════════════════════ */}
      <section className="bg-gray-50 py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-extrabold text-[#001a4d] mb-3">
            Who we serve
          </h2>
          <p className="text-gray-600 text-base mb-12">
            Serving Baltimore and the surrounding Maryland region.
          </p>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-6">
            {industries.map((ind, i) => {
              const Icon = ind.icon;
              return (
                <div key={i} className="flex flex-col items-center gap-3">
                  <div className="w-14 h-14 rounded-2xl bg-[#001a4d] flex items-center justify-center">
                    <Icon className="w-7 h-7 text-white" strokeWidth={1.5} />
                  </div>
                  <span className="text-xs font-semibold text-gray-700 text-center leading-tight">{ind.label}</span>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════
          6. MARYLAND BMC TAX CREDIT — teal bg
         ═══════════════════════════════════════════════════════ */}
      <section className="bg-[#0B8A8F] py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/20 mb-5">
            <CheckCircle size={15} className="text-white" />
            <span className="text-white text-xs font-bold uppercase tracking-wide">Maryland Tax Credit</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-extrabold text-white mb-5">
            Maryland pays back 50% of what you spend on cybersecurity
          </h2>
          <p className="text-white/90 text-base leading-relaxed mb-6">
            The Buy Maryland Cybersecurity (BMC) Tax Credit reimburses qualifying Maryland
            small businesses <strong className="text-white">50% of cybersecurity costs</strong> as
            a direct income tax credit. Ask us how to claim it.
          </p>
          <a
            href="mailto:info@cyvantixsolutions.com?subject=Maryland%20BMC%20Tax%20Credit%20Question"
            className="inline-flex items-center gap-2 px-8 py-4 bg-white text-[#0B8A8F] font-bold rounded-xl hover:bg-gray-50 transition-all shadow-md text-base"
          >
            Ask Us About the BMC Credit
            <ArrowRight size={18} />
          </a>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════
          7. ABOUT
         ═══════════════════════════════════════════════════════ */}
      <section className="bg-white py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
            {/* Left: text */}
            <div>
              <h2 className="text-3xl md:text-4xl font-extrabold text-[#001a4d] mb-6 leading-tight">
                Federal expertise. Baltimore roots. Built for small business.
              </h2>
              <div className="space-y-4 text-gray-600 text-base leading-relaxed">
                <p>
                  Cyvantix Solutions was founded by cybersecurity professionals who spent years
                  protecting federal agencies and large defense contractors at the highest levels — then
                  looked around and saw that the small businesses running Baltimore had no one in their corner.
                </p>
                <p>
                  We built CyvaForge, our proprietary security assessment platform, specifically for
                  businesses without a dedicated IT security team. It lets us deliver the same quality
                  of assessment that large enterprises pay six figures for — at a price a dental
                  practice or law firm can actually afford.
                </p>
                <p>
                  Our team includes certified IT professionals holding CompTIA A+ and Security+
                  certifications alongside our federal cybersecurity background — so whether you
                  need day-to-day IT support or enterprise-grade security assessments, we have the
                  credentials and experience to back it up.
                </p>
                <p>
                  We are a Maryland-based, woman-owned small business. We know this market, we live
                  in this community, and we are invested in keeping Baltimore businesses safe.
                </p>
              </div>
              <p className="mt-5 text-sm text-gray-400 italic">
                Our assessments run on CyvaForge, our proprietary AI security platform built in-house — not off-the-shelf tools.
              </p>
            </div>

            {/* Right: credential badges */}
            <div className="grid grid-cols-1 gap-4">
              {stats.map((stat, i) => (
                <div
                  key={i}
                  className="flex items-start gap-4 bg-gray-50 border border-gray-100 rounded-2xl px-6 py-5"
                >
                  <div className="w-10 h-10 rounded-xl bg-[#0B8A8F] flex items-center justify-center flex-shrink-0 mt-0.5">
                    <CheckCircle className="w-5 h-5 text-white" strokeWidth={2.5} />
                  </div>
                  <div>
                    <div className="font-bold text-[#001a4d] text-sm">{stat.label}</div>
                    <div className="text-gray-500 text-xs mt-0.5">{stat.sub}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════
          8. CTA — navy bg
         ═══════════════════════════════════════════════════════ */}
      <section className="bg-[#001a4d] py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-extrabold text-white mb-4">
            Ready to see where you stand?
          </h2>
          <p className="text-white/70 text-base mb-10">
            Book a free 20-minute CyvaCheck — no obligation, no jargon, just answers.
          </p>

          <a
            href="mailto:info@cyvantixsolutions.com?subject=Book%20Free%20CyvaCheck"
            className="inline-flex items-center gap-2 px-8 py-4 bg-[#0B8A8F] text-white font-bold rounded-xl hover:bg-[#097a7f] transition-all shadow-lg text-base mb-10"
          >
            Book Your Free CyvaCheck
            <ArrowRight size={18} />
          </a>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-6 text-white/60 text-sm">
            <a href="mailto:info@cyvantixsolutions.com" className="flex items-center gap-2 hover:text-white transition-colors">
              <Mail size={15} />
              info@cyvantixsolutions.com
            </a>
            <span className="flex items-center gap-2">
              <MapPin size={15} />
              Baltimore, MD
            </span>
          </div>
        </div>
      </section>

    </div>
  );
};

export default Home;
